<script setup lang="ts">
/**
 * A round button with an icon: the meeting's controls, the rail's links,
 * a panel's close. `label` is what a screen reader says and the tooltip
 * shows. `tone`: `plain` on a page, `stage` for the call's controls (a grey
 * disc, white icon), `on` when the thing it toggles is active, `off` when it
 * is off in a way that matters (a muted mic, in red as Meet has it),
 * `danger` to hang up.
 */
withDefaults(
  defineProps<{
    label: string
    tone?: 'plain' | 'stage' | 'on' | 'off' | 'danger' | 'primary'
    size?: 'xs' | 'sm' | 'md' | 'lg'
    pressed?: boolean
    disabled?: boolean
  }>(),
  { tone: 'plain', size: 'md' },
)
</script>

<template>
  <button
    type="button"
    :aria-label="label"
    :title="label"
    :aria-pressed="pressed"
    :disabled="disabled"
    class="inline-flex shrink-0 items-center justify-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-45"
    :class="[
      { xs: 'size-7', sm: 'size-9', md: 'size-11', lg: 'size-13' }[size],
      {
        plain: 'text-muted hover:bg-surface-muted hover:text-fg',
        stage: 'bg-stage-control text-white hover:bg-stage-control-hover',
        on: 'bg-primary-soft text-primary hover:bg-primary-soft/80',
        off: 'bg-danger-soft text-danger hover:bg-danger/20',
        danger: 'bg-danger text-white hover:bg-danger-hover',
        primary: 'bg-primary text-primary-fg hover:bg-primary-hover',
      }[tone],
    ]"
  >
    <slot />
  </button>
</template>
